// SkipInventoryListPage — /skip/inventory.

import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Plus } from 'lucide-react';
import { Card } from '../../../components/ui/Card/Card';
import { Button } from '../../../components/ui/Button/Button';
import { PageHeader } from '../../../components/ui/PageHeader/PageHeader';
import { useAuthStore } from '../../../store/authStore';
import { useSkipInventory } from '../hooks/useSkipInventory';
import { SkipInventoryStatusBadge } from './components/SkipInventoryStatusBadge';
import type { SkipInventorySummary } from '../../../types/api';
import styles from './SkipInventoryListPage.module.css';

const STATUSES = [
  { value: '', label: 'All statuses' },
  { value: 'available', label: 'Available' },
  { value: 'out', label: 'Out' },
  { value: 'maintenance', label: 'Maintenance' },
];

function daysOut(deliveredAt: string | null): number | null {
  if (!deliveredAt) return null;
  const d = new Date(deliveredAt);
  if (Number.isNaN(d.getTime())) return null;
  const ms = Date.now() - d.getTime();
  return Math.floor(ms / 86_400_000);
}

export function SkipInventoryListPage(): JSX.Element {
  const navigate = useNavigate();
  const role = useAuthStore((s) => s.user?.role);
  const isAdmin = role === 'admin';

  const [status, setStatus] = useState('');
  const [page, setPage] = useState(1);

  const filters: Record<string, string | number> = { page, limit: 25 };
  if (status) filters['status'] = status;

  const inv = useSkipInventory(filters);

  return (
    <div className={styles.page}>
      <PageHeader
        title="Inventory"
        subtitle="Track and manage all skip / bin / roro units"
        actions={
          isAdmin ? (
            <Button
              variant="primary"
              icon={Plus}
              onClick={() => navigate('/skip/inventory/create')}
            >
              Add Unit
            </Button>
          ) : null
        }
      />

      <Card className={styles.filters}>
        <div className={styles.filterRow}>
          <select
            value={status}
            onChange={(e) => {
              setStatus(e.target.value);
              setPage(1);
            }}
            className={styles.statusSelect}
            aria-label="Filter by status"
          >
            {STATUSES.map((s) => (
              <option key={s.value} value={s.value}>
                {s.label}
              </option>
            ))}
          </select>
        </div>
      </Card>

      <Card>
        {inv.isLoading ? (
          <p className={styles.empty}>Loading…</p>
        ) : (inv.data?.inventory.length ?? 0) === 0 ? (
          <p className={styles.empty}>No inventory units match your filters.</p>
        ) : (
          <table className={styles.table}>
            <thead>
              <tr>
                <th>Unit #</th>
                <th>Type</th>
                <th>Size</th>
                <th>Status</th>
                <th>On Job</th>
                <th>Days Out</th>
                <th aria-label="Actions" />
              </tr>
            </thead>
            <tbody>
              {inv.data?.inventory.map((i: SkipInventorySummary) => {
                const days = daysOut(i.active_job_delivered_at);
                const daysClass = days === null ? '' : days > 30 ? styles.daysHigh : days > 14 ? styles.daysMid : '';
                return (
                  <tr key={i.enc_id}>
                    <td className={styles.cellMono}>
                      <Link to={`/skip/inventory/${encodeURIComponent(i.enc_id)}`}>
                        {i.skip_number}
                      </Link>
                    </td>
                    <td>{i.stock_type_name ?? '—'}</td>
                    <td>{i.size_name ?? '—'}</td>
                    <td>
                      <SkipInventoryStatusBadge status={i.effective_status} />
                    </td>
                    <td>
                      {i.active_job_number ? (
                        <Link
                          to={
                            i.active_job_uuid
                              ? `/skip/jobs/${encodeURIComponent(i.active_job_uuid)}`
                              : '#'
                          }
                        >
                          {i.active_job_number}
                        </Link>
                      ) : (
                        '—'
                      )}
                    </td>
                    <td className={daysClass}>{days !== null ? `${days}d` : '—'}</td>
                    <td className={styles.actions}>
                      <Link to={`/skip/inventory/${encodeURIComponent(i.enc_id)}`}>View</Link>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}

        {inv.data && inv.data.pagination.totalPages > 1 ? (
          <div className={styles.pagination}>
            <Button
              variant="secondary"
              disabled={page <= 1}
              onClick={() => setPage((p) => Math.max(1, p - 1))}
            >
              Previous
            </Button>
            <span className={styles.pageInfo}>
              Page {page} of {inv.data.pagination.totalPages} ({inv.data.pagination.total} total)
            </span>
            <Button
              variant="secondary"
              disabled={page >= inv.data.pagination.totalPages}
              onClick={() => setPage((p) => p + 1)}
            >
              Next
            </Button>
          </div>
        ) : null}
      </Card>
    </div>
  );
}
